import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DownloadIcon from "@mui/icons-material/Download";
import ApexCharts from "apexcharts";
import { getAssistantChart } from "@Utils/assistantChartsStorage";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { GeneratedApexChartRenderer } from "@Components/Charts/GeneratedApexChartRenderer";

type AssistantChartPreviewDialogProps = {
    channelId: string;
    chartId: string;
};

const normalizeFilenamePart = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    return trimmed
        .replace(/[^\w\d-]+/g, "_")
        .replace(/_{2,}/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 64);
};

const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
};

const AssistantChartPreviewDialogContent: React.FC<
    AssistantChartPreviewDialogProps
> = ({ channelId, chartId }) => {
    const { t } = useTranslation();
    const stored = useMemo(() => {
        if (!chartId) return null;
        return getAssistantChart(chartId);
    }, [chartId]);

    const hasChart = Boolean(stored?.chart);
    const channelMismatch = Boolean(
        stored &&
            stored.channelId &&
            channelId &&
            stored.channelId !== channelId
    );

    return (
        <Box sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
            {!stored ? (
                <>
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                        {t(
                            "pages.eventInsights.assistant.charts.notAvailableTitle"
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            "pages.eventInsights.assistant.charts.notAvailableBody"
                        )}
                    </Typography>
                </>
            ) : channelMismatch ? (
                <>
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                        {t(
                            "pages.eventInsights.assistant.charts.mismatchTitle"
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("pages.eventInsights.assistant.charts.mismatchBody")}
                    </Typography>
                </>
            ) : !hasChart ? (
                <>
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                        {t(
                            "pages.eventInsights.assistant.charts.renderErrorTitle"
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            "pages.eventInsights.assistant.charts.renderErrorBody"
                        )}
                    </Typography>
                </>
            ) : (
                <>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        display="block"
                    >
                        {t(
                            "pages.eventInsights.assistant.charts.generatedVisualization"
                        )}
                    </Typography>
                    <Typography variant="h6" fontWeight={900} gutterBottom>
                        {stored.chart.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {stored.chart.whyItMatters}
                    </Typography>

                    <Box sx={{ width: "100%", minHeight: 420 }}>
                        <GeneratedApexChartRenderer
                            chart={stored.chart}
                            height={420}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default {
    content: AssistantChartPreviewDialogContent,
    actions: ({ channelId, chartId }: AssistantChartPreviewDialogProps) => {
        const { t } = useTranslation();
        const dispatch = useDispatch<any>();
        const [downloading, setDownloading] = useState(false);

        const stored = useMemo(() => {
            if (!chartId) return null;
            return getAssistantChart(chartId);
        }, [chartId]);

        const canDownload = Boolean(
            stored?.chart?.id && stored.channelId === channelId
        );

        const handleDownload = useCallback(async () => {
            if (!stored?.chart?.id) return;
            if (stored.channelId !== channelId) return;
            if (downloading) return;

            setDownloading(true);
            try {
                const result = await ApexCharts.exec(
                    stored.chart.id,
                    "dataURI",
                    { scale: 2 }
                );

                const blob = (result as any)?.blob;
                const imgURI = (result as any)?.imgURI;
                const titlePart = normalizeFilenamePart(stored.chart.title);
                const fallbackPart = normalizeFilenamePart(stored.chart.id);
                const baseName = titlePart || fallbackPart || "assistant_chart";
                const filename = `${baseName}.png`;

                if (blob instanceof Blob) {
                    downloadBlob(blob, filename);
                } else if (typeof imgURI === "string" && imgURI.length > 0) {
                    const anchor = document.createElement("a");
                    anchor.href = imgURI;
                    anchor.download = filename;
                    document.body.appendChild(anchor);
                    anchor.click();
                    document.body.removeChild(anchor);
                }

                dispatch(dialogActions.hideDialog());
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(
                    "Failed to download assistant chart image.",
                    error
                );
            } finally {
                setDownloading(false);
            }
        }, [channelId, dispatch, downloading, stored]);

        if (!canDownload) return <></>;

        return (
            <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={downloading}
                startIcon={<DownloadIcon />}
            >
                {t("pages.eventInsights.assistant.charts.downloadImageButton")}
            </Button>
        );
    },
    title: "Generated Chart",
    showActons: true,
    showHeader: false,
    disableCancelButton: true,
    showCloseButton: true,
};
